import React, { memo, useCallback } from "react";
import { Edit2, Eye, ShieldCheck, ShieldAlert } from "lucide-react";
import { ChannelListItem } from "@repo/types";
import moment from "moment";

interface ChannelTableProps {
  loading: boolean;
  channels: ChannelListItem[];
  onEdit: (channel: ChannelListItem) => void;
  onView: (channel: ChannelListItem) => void;
}

// ── Memoized row to prevent mass re-renders when other rows change ──
interface ChannelRowProps {
  channel: ChannelListItem;
  onEdit: (channel: ChannelListItem) => void;
  onView: (channel: ChannelListItem) => void;
}

const ChannelRow: React.FC<ChannelRowProps> = memo(
  ({ channel, onEdit, onView }) => {
    const handleView = useCallback(() => onView(channel), [channel, onView]);
    const handleEdit = useCallback(() => onEdit(channel), [channel, onEdit]);
    const formattedDate = moment(channel.CreatedDate).format("DD MMM YYYY");

    return (
      <tr
        key={channel.ChannelID}
        className="group hover:bg-slate-50 dark:hover:bg-[#1b254b]/30 transition-colors border-b border-slate-100 dark:border-white/10 last:border-none"
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#1b254b] text-white flex items-center justify-center font-bold text-sm shadow-sm group-hover:scale-105 transition-transform">
              {channel.ChannelCode.substring(0, 2)}
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                {channel.ChannelName}
              </div>
              <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-0.5">
                {channel.ChannelCode}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-bold ring-1 ring-orange-600/20 dark:ring-orange-800/40">
            P-{channel.Priority}
          </div>
        </td>
        <td className="px-6 py-4">
          {channel.IsCredentialsEncrypted ? (
            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <ShieldCheck size={16} />
              <span className="text-xs font-semibold">Encrypted</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-amber-500 dark:text-amber-400">
              <ShieldAlert size={16} />
              <span className="text-xs font-semibold">Plain</span>
            </div>
          )}
        </td>
        <td className="px-6 py-4">
          {channel.IsActive ? (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs font-semibold ring-1 ring-emerald-600/20 dark:ring-emerald-800/40">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-50 dark:bg-[#1b254b]/50 text-slate-600 dark:text-slate-400 text-xs font-semibold ring-1 ring-slate-200 dark:ring-white/10">
              Inactive
            </span>
          )}
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {formattedDate}
          </div>
        </td>
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-2 transition-opacity">
            <button
              onClick={handleView}
              className="flex items-center justify-center h-9 w-9 p-0 rounded-xl text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
              title="View Details"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={handleEdit}
              className="flex items-center justify-center h-9 w-9 p-0 rounded-xl text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
              title="Edit Channel"
            >
              <Edit2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  },
);
ChannelRow.displayName = "ChannelRow";

// ── Main table component ──
export const ChannelTable: React.FC<ChannelTableProps> = memo(
  ({ loading, channels, onEdit, onView }) => {
    if (loading) {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4">
          <div className="w-8 h-8 border-4 border-slate-200 dark:border-white/10 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin" />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            Accessing Channel Registry...
          </p>
        </div>
      );
    }

    if (channels.length === 0) {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-[#1b254b] flex items-center justify-center text-slate-400 dark:text-slate-500 border border-slate-100 dark:border-white/5">
            <Eye size={32} />
          </div>
          <div className="space-y-1">
            <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
              No Channels Found
            </p>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium max-w-sm">
              Try adjusting your filters or search query to find what you're
              looking for.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-[#111c44] border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-[#1b254b]/50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Channel
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Priority
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Security
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Created
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-white/10">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {channels.map((channel) => (
                <ChannelRow
                  key={channel.ChannelID}
                  channel={channel}
                  onEdit={onEdit}
                  onView={onView}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);
ChannelTable.displayName = "ChannelTable";
